import * as React from "react"
import {Column} from "material-table"
import {ReportTakeRow} from "../../../reports/class/models/ReportTakeRow"
import {UnstyledLink} from "../../../generic-components/UnstyledLink"
import {IconButton} from "@material-ui/core"
import {Assessment} from "@material-ui/icons"
import {TimeUtil} from "../../../../util/TimeUtil"
import {MAX_DISPLAYABLE_TEST_DURATION} from "../../../reports/ReportUtils"
import {theme} from "../../../../styles/MuiThemes"
import {PanelLevel, PanelSkillEnum} from "../../../../types/types"
import {
    AvantBadgeEarlyExit, AvantBadgeNotRatable,
    AvantBadgeNotStarted,
    AvantBadgeScoredByEducator, AvantBadgeScoringInProgress, AvantBadgeSectionInProgress
} from "../../../generic-components/AvantBadge"
import {groupMasterStore} from "../../../common/group/GroupMasterStore"
import {reportsStore} from "../../../reports/common/stores/ReportStore"
import {ReportSubPaths} from "../../../reports/ReportSubPaths"
import {productStore} from "../../../common/products/ProductStore"
import {PLACE_PRODUCT_ID} from "../../../../util/Constants"
import {findPLACEIndividualReport} from "../../../reports/PlaceFamily/PLACEReportPage"
import RenderColumn, {TestTakerTableCell} from "./TestTakerTableCell"


export interface SkillColumnData {
    title: string
    key: string
    symbol?: PanelSkillEnum,
    scaledScoreKey?: string
    noWrap?: boolean
    centerContent?: boolean
    styles?: React.CSSProperties
}

function getBadgeOrScore(
    scaledScoreCalc: boolean,
    skillValNullable?: string,
    scaledValNullable?: string,
    isPlaceC?: boolean,
    isSHL?: boolean
) {
    const skillVal = skillValNullable == null ? "" : skillValNullable
    const scaledVal = scaledValNullable == null ? "" : scaledValNullable
    if ((skillVal === PanelLevel.PANEL_NOT_STARTED || skillVal === "") && scaledVal === "") {
        if (isSHL) {
            return <AvantBadgeEarlyExit/>
        }

        return <AvantBadgeNotStarted/>
    }
    if (skillVal === PanelLevel.PANEL_COMPLETE && isPlaceC) {
        return <AvantBadgeScoredByEducator/>
    }
    if (skillVal === PanelLevel.PANEL_COMPLETE && scaledVal === "") {
        return <AvantBadgeScoringInProgress/>
    }
    if (skillVal === PanelLevel.PANEL_STARTED && scaledVal === "") {
        return <AvantBadgeSectionInProgress/>
    }
    if (skillVal === PanelLevel.PANEL_NOT_RATABLE && scaledVal === "") {
        return <AvantBadgeNotRatable/>
    }
    if (scaledVal === "" && scaledScoreCalc) {
        return ""
    }
    if (scaledVal !== "") {
        return parseInt(scaledVal)
    }
    return skillVal
}

function rowDurationString(duration?: number): string {
    if (!duration) return ""

    if (duration > MAX_DISPLAYABLE_TEST_DURATION) {
        return `${MAX_DISPLAYABLE_TEST_DURATION}`
    }

    return duration.toString()
}

// Returns stringified data that will be passed down to
// individual reports via the react link's state
function additionalReportData(row: ReportTakeRow): string {
    const productId = (productStore.loginProduct) ? productStore.loginProduct.productId : null
    switch (productId) {
        case PLACE_PRODUCT_ID.value():
            return findPLACEIndividualReport(row.takeId)
        default:
            return JSON.stringify(groupMasterStore.groupInfo)
    }
}

const baseLeftTableColumns: Column<any>[] = [
    {
        title: "Report",
        render: (row: ReportTakeRow) => {
            return (
                <TestTakerTableCell>
                    <UnstyledLink
                        to={{
                            // TODO: Make this link work
                            pathname: ReportSubPaths.individual(row.takeId, reportsStore.searchFilters),
                            state: {report: additionalReportData(row)}
                        }}
                        target={['pl', 'pl-b', 'pl-c'].includes(row.product_package!) ? '_blank' : ''}
                    >
                        <IconButton
                            size={"small"}
                            aria-label="Student Summary"
                        >
                            <Assessment/>
                        </IconButton>
                    </UnstyledLink>
                </TestTakerTableCell>
            )
        },
    },
    {
        title: "Last Name",
        defaultSort: "asc",
        render: (row: ReportTakeRow) => RenderColumn(row.lastName, true, {
            title: "",
            key: "",
            styles: {textAlign: "left"}
        }, row),
    },
    {
        title: "First Name",
        render: (row: ReportTakeRow) => RenderColumn(row.firstName),
    },
]

const baseRightTableColumns: Column<any>[] = [
    {
        title: "Login Name",
        render: (row: ReportTakeRow) => RenderColumn(row.resumeKey, true),
    },
    {
        title: "Composite Score",
        render: (row: ReportTakeRow) => RenderColumn(
            row.compositeScore !== 0 && row.compositeScore != null ? row.compositeScore : "",
            false,
        )
    },
    {
        title: "Start Date",
        render: (row: ReportTakeRow) => RenderColumn(
            TimeUtil.formatMilliToMonthDayYearHoursMinutesLong(row.startDate)
        )
    },
    {
        title: "Finish Date",
        render: (row: ReportTakeRow) => RenderColumn(
            TimeUtil.formatMilliToMonthDayYearHoursMinutesLong(row.finishDate)
        )
    },
    {
        title: "Length",
        render: (row: ReportTakeRow) => RenderColumn(rowDurationString(row.testDuration)),
    },
]

export function buildReportColumns(skillColumns: SkillColumnData[]): {left: Column<any>[], right: Column<any>[]} {
    const columns = {left: [...baseLeftTableColumns], right: [...baseRightTableColumns]}

    columns.left.forEach((column: Column<any>) => {
        column.cellStyle = {
            height: 52,
            padding: theme.spacing(0, 1),
            whiteSpace: "nowrap",
        }
    })

    const skills: Column<any>[] = []
    if (skillColumns) {
        skillColumns.forEach((skill: SkillColumnData) => {
            const column: Column<any> = {
                title: skill.title,
            }

            if (skill.title.includes("Scaled")) {
                column.render = (row: ReportTakeRow) => {
                    if (row[skill.scaledScoreKey!]) {
                        return RenderColumn(
                            getBadgeOrScore(true, row[skill.key], row[skill.scaledScoreKey!]),
                            false,
                            skill
                        )
                    } else {
                        return null
                    }
                }

                skills.push(column)
                return
            }

            column.render = (row: ReportTakeRow) => RenderColumn(
                getBadgeOrScore(false, row[skill.key]), true, skill, row
            )

            skills.push(column)
        })

        // Add the skill columns after the login name
        columns.right.splice(
            columns.right.map((col: Column<any>) => col.title).indexOf("Composite Score"),
            0,
            ...skills
        )
    }

    columns.right.forEach((column: Column<any>) => {
        column.cellStyle = {
            height: 52,
            padding: theme.spacing(0, 1),
            whiteSpace: "nowrap",
        }
    })

    return columns
}
